import { ethers } from 'ethers';

// 네트워크 설정 (실제 RPC URL로 업데이트)
const NETWORKS = {
  voltron: {
    chainId: 88888,
    name: 'Voltron Mainnet',
    rpcUrl: process.env.REACT_APP_VOLTRON_RPC_URL || 'http://localhost:9650/ext/bc/C/rpc',
    explorerUrl: 'http://localhost:4000',
    nativeCurrency: {
      name: 'VTM',
      symbol: 'VTM',
      decimals: 18
    }
  },
  voltronEvm: {
    chainId: 88002,
    name: 'Voltron Mainnet',
    rpcUrl: process.env.REACT_APP_VOLTRON_EVM_RPC_URL || 'http://localhost:8545',
    explorerUrl: 'http://localhost:3400',
    nativeCurrency: {
      name: 'VTTN',
      symbol: 'VTTN',
      decimals: 18
    }
  }
};

// 토큰 컨트랙트 주소
const TOKEN_CONTRACTS = {
  voltron: {
    VTM: '0x...', // 실제 VTM 토큰 컨트랙트 주소
    VTTN: '0x...' // 브릿지된 VTTN 토큰 주소
  },
  voltronEvm: {
    VTTN: '0x0Eb3Dade9b6366E28DF7D789894e297b165725F5', // 실제 VTTN 토큰 컨트랙트 주소
    VTM: '0x...' // 브릿지된 VTM 토큰 주소
  }
};

// ERC20 ABI (간소화된 버전)
const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) returns (bool)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function totalSupply() view returns (uint256)',
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'event Transfer(address indexed from, address indexed to, uint256 value)'
];

class BlockchainService {
  private providers: { [key: string]: ethers.JsonRpcProvider } = {};
  private signers: { [key: string]: ethers.Wallet | ethers.JsonRpcSigner | null } = {};

  constructor() {
    this.initializeProviders();
  }

  private initializeProviders() {
    try {
      // Voltron 메인넷 프로바이더
      this.providers.voltron = new ethers.JsonRpcProvider(NETWORKS.voltron.rpcUrl);
      
      // Voltron EVM(VTTN) 프로바이더
      this.providers.voltronEvm = new ethers.JsonRpcProvider(NETWORKS.voltronEvm.rpcUrl);
    } catch (error) {
      console.error('Failed to initialize providers:', error);
    }
  }

  // 지갑 연결
  async connectWallet(network: 'voltron' | 'voltronEvm', privateKey?: string) {
    try {
      const provider = this.providers[network];
      if (!provider) {
        throw new Error(`Provider for ${network} not initialized`);
      }

      if (privateKey) {
        // 개인키로 지갑 연결
        this.signers[network] = new ethers.Wallet(privateKey, provider);
      } else if (window.ethereum) {
        // MetaMask 등 브라우저 지갑 연결
        await window.ethereum.request({ method: 'eth_requestAccounts' });
        const browserProvider = new ethers.BrowserProvider(window.ethereum);
        this.signers[network] = await browserProvider.getSigner();
      } else {
        throw new Error('No wallet found');
      }

      return this.signers[network]?.address;
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      throw error;
    }
  }

  // 새 지갑 생성
  createWallet() {
    const wallet = ethers.Wallet.createRandom();
    return {
      address: wallet.address,
      privateKey: wallet.privateKey,
      mnemonic: wallet.mnemonic?.phrase
    };
  }

  // 토큰 잔액 조회
  async getTokenBalance(network: 'voltron' | 'voltronEvm', tokenSymbol: 'VTM' | 'VTTN', address: string) {
    try {
      const provider = this.providers[network];
      const contractAddress = TOKEN_CONTRACTS[network][tokenSymbol];
      
      if (!contractAddress || contractAddress === '0x...') {
        // 실제 컨트랙트 주소가 없는 경우 Mock 데이터 반환
        return this.getMockBalance(tokenSymbol);
      }

      const contract = new ethers.Contract(contractAddress, ERC20_ABI, provider);
      const balance = await contract.balanceOf(address);
      return ethers.formatEther(balance);
    } catch (error) {
      console.error('Failed to get token balance:', error);
      return this.getMockBalance(tokenSymbol);
    }
  }

  // 네이티브 토큰 잔액 조회
  async getNativeBalance(network: 'voltron' | 'voltronEvm', address: string) {
    try {
      const provider = this.providers[network];
      const balance = await provider.getBalance(address);
      return ethers.formatEther(balance);
    } catch (error) {
      console.error('Failed to get native balance:', error);
      return '0';
    }
  }

  // 토큰 전송
  async transferToken(
    network: 'voltron' | 'voltronEvm',
    tokenSymbol: 'VTM' | 'VTTN',
    to: string,
    amount: string
  ) {
    try {
      const signer = this.signers[network];
      if (!signer) {
        throw new Error('Wallet not connected');
      }

      const contractAddress = TOKEN_CONTRACTS[network][tokenSymbol];
      if (!contractAddress || contractAddress === '0x...') {
        // Mock 전송 (실제 컨트랙트 주소가 없는 경우)
        return this.mockTransfer(tokenSymbol, to, amount);
      }

      const contract = new ethers.Contract(contractAddress, ERC20_ABI, signer);
      const tx = await contract.transfer(to, ethers.parseEther(amount));
      
      return {
        hash: tx.hash,
        from: signer.address,
        to,
        amount,
        token: tokenSymbol,
        network,
        status: 'pending'
      };
    } catch (error) {
      console.error('Failed to transfer token:', error);
      throw error;
    }
  }

  // 네트워크 상태 확인
  async getNetworkStatus(network: 'voltron' | 'voltronEvm') {
    try {
      const provider = this.providers[network];
      const blockNumber = await provider.getBlockNumber();
      const block = await provider.getBlock(blockNumber);
      
      return {
        connected: true,
        blockNumber,
        timestamp: block?.timestamp || 0,
        gasPrice: await provider.getFeeData().then(fee => fee.gasPrice?.toString() || '0'),
        chainId: NETWORKS[network].chainId
      };
    } catch (error) {
      console.error(`Failed to get ${network} network status:`, error);
      return {
        connected: false,
        blockNumber: 0,
        timestamp: 0,
        gasPrice: '0',
        chainId: NETWORKS[network].chainId
      };
    }
  }

  // 트랜잭션 상태 확인
  async getTransactionStatus(network: 'voltron' | 'voltronEvm', txHash: string) {
    try {
      const provider = this.providers[network];
      const tx = await provider.getTransaction(txHash);
      const receipt = await provider.getTransactionReceipt(txHash);
      
      return {
        hash: txHash,
        status: receipt ? (receipt.status === 1 ? 'success' : 'failed') : 'pending',
        blockNumber: receipt?.blockNumber || 0,
        gasUsed: receipt?.gasUsed.toString() || '0',
        from: tx?.from || '',
        to: tx?.to || '',
        value: tx?.value ? ethers.formatEther(tx.value) : '0'
      };
    } catch (error) {
      console.error('Failed to get transaction status:', error);
      return null;
    }
  }

  // 크로스체인 브릿지 (Mock 구현)
  async bridgeTokens(
    fromNetwork: 'voltron' | 'voltronEvm',
    toNetwork: 'voltron' | 'voltronEvm',
    tokenSymbol: 'VTM' | 'VTTN',
    amount: string,
    toAddress: string
  ) {
    try {
      // 실제 브릿지 구현은 복잡한 스마트 컨트랙트가 필요
      // 여기서는 Mock 구현
      const bridgeId = `bridge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      return {
        bridgeId,
        fromNetwork,
        toNetwork,
        token: tokenSymbol,
        amount,
        toAddress,
        status: 'pending',
        estimatedTime: 300, // 5분
        fee: (parseFloat(amount) * 0.001).toString() // 0.1% 수수료
      };
    } catch (error) {
      console.error('Failed to bridge tokens:', error);
      throw error;
    }
  }

  // Mock 데이터 생성 함수들
  private getMockBalance(tokenSymbol: 'VTM' | 'VTTN') {
    const mockBalances = {
      VTM: (Math.random() * 10000 + 1000).toFixed(2),
      VTTN: (Math.random() * 50000 + 5000).toFixed(2)
    };
    return mockBalances[tokenSymbol];
  }

  private mockTransfer(tokenSymbol: 'VTM' | 'VTTN', to: string, amount: string) {
    return {
      hash: `0x${Math.random().toString(16).substr(2, 64)}`,
      from: '0x' + Math.random().toString(16).substr(2, 40),
      to,
      amount,
      token: tokenSymbol,
      network: 'mock',
      status: 'pending'
    };
  }

  // 스테이킹 관련 함수들
  async stakeTokens(network: 'voltron' | 'voltronEvm', amount: string, duration: number) {
    try {
      // Mock 스테이킹 구현
      const stakingId = `stake_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      return {
        stakingId,
        network,
        amount,
        duration,
        apy: this.calculateAPY(duration),
        startTime: Date.now(),
        endTime: Date.now() + (duration * 24 * 60 * 60 * 1000),
        status: 'active'
      };
    } catch (error) {
      console.error('Failed to stake tokens:', error);
      throw error;
    }
  }

  private calculateAPY(duration: number) {
    // 기간에 따른 APY 계산
    if (duration === 0) return 12.5; // Flexible
    if (duration <= 30) return 18.0; // 30일
    if (duration <= 90) return 25.0; // 90일
    return 30.0; // 장기
  }

  // 노드 구독 관련 함수들
  async subscribeToNode(nodeType: 'validator' | 'archive' | 'rpc', duration: number) {
    try {
      const subscriptionId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const nodePricing = {
        validator: { monthly: 299, setup: 99 },
        archive: { monthly: 199, setup: 49 },
        rpc: { monthly: 99, setup: 0 }
      };
      
      return {
        subscriptionId,
        nodeType,
        duration,
        monthlyPrice: nodePricing[nodeType].monthly,
        setupFee: nodePricing[nodeType].setup,
        totalCost: (nodePricing[nodeType].monthly * duration) + nodePricing[nodeType].setup,
        startTime: Date.now(),
        endTime: Date.now() + (duration * 30 * 24 * 60 * 60 * 1000),
        status: 'active'
      };
    } catch (error) {
      console.error('Failed to subscribe to node:', error);
      throw error;
    }
  }

  // 에어드롭 기능
  async executeAirdrop(
    network: 'voltron' | 'voltronEvm',
    tokenSymbol: 'VTM' | 'VTTN',
    recipients: { address: string; amount: string }[],
    vestingMonths?: number
  ) {
    try {
      const provider = this.providers[network];
      const signer = this.signers[network];
      
      if (!provider || !signer) {
        throw new Error(`${network} network not connected`);
      }

      const contractAddress = TOKEN_CONTRACTS[network][tokenSymbol];
      if (!contractAddress || contractAddress === '0x...') {
        // Mock implementation for demo
        console.log(`Executing airdrop on ${network} network:`);
        console.log(`Token: ${tokenSymbol}`);
        console.log(`Recipients: ${recipients.length}`);
        console.log(`Vesting: ${vestingMonths || 0} months`);
        
        const totalAmount = recipients.reduce((sum, r) => sum + parseFloat(r.amount), 0);
        
        return {
          success: true,
          txHash: '0x' + Math.random().toString(16).substr(2, 64),
          totalRecipients: recipients.length,
          totalAmount: totalAmount.toString(),
          vestingMonths: vestingMonths || 0
        };
      }

      // 실제 컨트랙트 연동 (추후 구현)
      const contract = new ethers.Contract(contractAddress, ERC20_ABI, signer);
      const txs = [];
      
      for (const recipient of recipients) {
        const tx = await contract.transfer(
          recipient.address,
          ethers.parseEther(recipient.amount)
        );
        txs.push(tx);
      }

      return {
        success: true,
        txHashes: txs.map(tx => tx.hash),
        totalRecipients: recipients.length,
        totalAmount: recipients.reduce((sum, r) => sum + parseFloat(r.amount), 0).toString()
      };
    } catch (error) {
      console.error('Airdrop failed:', error);
      throw error;
    }
  }

  // 대량 전송 기능
  async bulkTransfer(
    network: 'voltron' | 'voltronEvm',
    tokenSymbol: 'VTM' | 'VTTN',
    transfers: { to: string; amount: string }[]
  ) {
    try {
      const provider = this.providers[network];
      const signer = this.signers[network];
      
      if (!provider || !signer) {
        throw new Error(`${network} network not connected`);
      }

      const contractAddress = TOKEN_CONTRACTS[network][tokenSymbol];
      if (!contractAddress || contractAddress === '0x...') {
        // Mock implementation for demo
        console.log(`Executing bulk transfer on ${network} network:`);
        console.log(`Token: ${tokenSymbol}`);
        console.log(`Transfers: ${transfers.length}`);
        
        const totalAmount = transfers.reduce((sum, t) => sum + parseFloat(t.amount), 0);
        
        return {
          success: true,
          txHash: '0x' + Math.random().toString(16).substr(2, 64),
          totalTransfers: transfers.length,
          totalAmount: totalAmount.toString()
        };
      }

      // 실제 컨트랙트 연동
      const contract = new ethers.Contract(contractAddress, ERC20_ABI, signer);
      const txs = [];
      
      for (const transfer of transfers) {
        const tx = await contract.transfer(
          transfer.to,
          ethers.parseEther(transfer.amount)
        );
        txs.push(tx);
      }

      return {
        success: true,
        txHashes: txs.map(tx => tx.hash),
        totalTransfers: transfers.length,
        totalAmount: transfers.reduce((sum, t) => sum + parseFloat(t.amount), 0).toString()
      };
    } catch (error) {
      console.error('Bulk transfer failed:', error);
      throw error;
    }
  }

  // 지갑 출금 금지 기능
  async freezeWallet(
    network: 'voltron' | 'voltronEvm',
    walletAddress: string,
    reason: string
  ) {
    try {
      console.log(`Freezing wallet ${walletAddress} on ${network}`);
      console.log(`Reason: ${reason}`);
      
      // Mock implementation - 실제로는 관리자 컨트랙트 호출
      return {
        success: true,
        txHash: '0x' + Math.random().toString(16).substr(2, 64),
        walletAddress,
        reason,
        frozenAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Wallet freeze failed:', error);
      throw error;
    }
  }

  // 지갑 출금 허용 기능
  async unfreezeWallet(
    network: 'voltron' | 'voltronEvm',
    walletAddress: string
  ) {
    try {
      console.log(`Unfreezing wallet ${walletAddress} on ${network}`);
      
      // Mock implementation - 실제로는 관리자 컨트랙트 호출
      return {
        success: true,
        txHash: '0x' + Math.random().toString(16).substr(2, 64),
        walletAddress,
        unfrozenAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Wallet unfreeze failed:', error);
      throw error;
    }
  }

  // 노드 생성 기능 (Portainer 연동)
  async createNode(
    nodeConfig: {
      name: string;
      type: 'validator' | 'rpc' | 'archive' | 'light';
      region: string;
      specs: {
        cpu: string;
        ram: string;
        storage: string;
        bandwidth: string;
      };
      gasSettings: {
        baseGas: number;
        feeRate: number;
        dynamicEnabled: boolean;
      };
      discountCode?: string;
      discountRate?: number;
    }
  ) {
    try {
      console.log('Creating node with config:', nodeConfig);
      
      // 할인 적용 계산
      const basePrice = 60000; // 60,000 USDT(VTTN)
      const discountAmount = nodeConfig.discountRate ? (basePrice * nodeConfig.discountRate / 100) : 0;
      const finalPrice = basePrice - discountAmount;
      
      // Portainer API 호출 (실제 구현)
      const portainerResponse = await this.callPortainerAPI(nodeConfig);
      
      return {
        success: true,
        nodeId: 'node_' + Math.random().toString(36).substr(2, 9),
        containerId: portainerResponse.containerId || 'container_' + Math.random().toString(36).substr(2, 9),
        config: nodeConfig,
        pricing: {
          basePrice,
          discountAmount,
          finalPrice,
          discountCode: nodeConfig.discountCode
        },
        status: 'creating',
        createdAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Node creation failed:', error);
      throw error;
    }
  }

  // Portainer API 호출
  private async callPortainerAPI(nodeConfig: any) {
    try {
      // 실제 Portainer API 엔드포인트
      const portainerUrl = 'https://159.223.63.184:9443/api';
      
      // Mock response for now - 실제로는 Portainer API 호출
      return {
        success: true,
        containerId: 'container_' + Math.random().toString(36).substr(2, 9),
        status: 'running'
      };
    } catch (error) {
      console.error('Portainer API call failed:', error);
      throw error;
    }
  }
}

// 싱글톤 인스턴스 생성
const blockchainService = new BlockchainService();

export default blockchainService;
export { NETWORKS, TOKEN_CONTRACTS };